import chatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import chatbotSidebarActions from '@mm/state/modules/chatbotSidebarState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import {
  activeChatbotIdSelector,
  activeCompanySelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { useLockBodyScroll } from '../../common/hooks/use-body-lock-scroll';
import { UserRoleContainer } from '../../common/hooks/use-route-guard';
import { BotBuilderRoutes } from '../../routes/bot-builder-routes';
import { ActivePageSlideMenu } from '../BotBuilderSidebar/ActivePageSlideMenu/ActivePageSlideMenu';
import { Logo } from '../BotBuilderSidebar/Logo/Logo';
import { SidebarMenuIconActions } from '../BotBuilderSidebar/SidebarHeader/SidebarMenuIconActions/SidebarMenuIconActions';
import { UpgradeRibbon } from '../BotBuilderSidebar/SidebarHeader/UpgradeRibbon/UpgradeRibbon';
import { SidebarMenuGroup } from '../BotBuilderSidebar/SidebarMenuGroup/SidebarMenuGroup';
import UserDropdown from '../BotBuilderSidebar/UserDropdown/UserDropdown';
import { filteredSidebarMenuSelector } from '../filteredSidebarMenuSelector';
import './MobileHeader.pcss';
import { AllFeaturesDropdown } from '../BotBuilderSidebar/SidebarHeader/AllFeaturesDropdown/AllFeaturesDropdown';
import { getIsAutomationFeatureEnabled } from '@mm/state/selectors/automations';
import { PageSwitcher } from '../BotBuilderSidebar/SidebarHeader/PageSwitcher/PageSwitcher';
import { HomeOutlined, LogoutOutlined, PlusOutlined } from '@ant-design/icons';
import { SIGNOUT } from '../../common/constants/routes';
import { useHistory } from 'react-router-dom';
import { AutomationTemplateContainer } from '../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/AutomationTemplateContainer';
import { TemplateGoButton } from '../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/TemplateGoButton';

type OuterProps = {};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const MobileHeader = (props: Props) => {
  const {
    activeFacebookPage,
    filteredSidebarMenu,
    activeChatbotId,
    toggleMenuGroupExpanded,
    expandedMenuGroups,
    company,
  } = props;

  const dispatch: Dispatch<StoreState> = useDispatch();
  const history = useHistory();

  const [slideMenuExpanded, setSlideMenuExpanded] = useState(false);
  const [dropdownExpanded, setDropdownExpanded] = useState(false);
  useLockBodyScroll(dropdownExpanded);
  const isAutomationsEnabled = useSelector(getIsAutomationFeatureEnabled);

  const planInfo = getPlanInfo(activeFacebookPage);
  const showUpgradeRibbon =
    !activeFacebookPage?.redemption?.freeTrial &&
    (planInfo.isFreeOrSpecialPlan || planInfo.isProPlan);
  const twilioNumber = useSelector(hasSMSNumberSelector);

  const {
    roleFlags: { isLiveChatAgent },
  } = UserRoleContainer.useContainer();

  const classList = classnames('mobile-header', {
    'mobile-header--expanded': dropdownExpanded,
    'mobile-header--page-selector-active': slideMenuExpanded,
  });

  const mobileSidebarMenu = useMemo(() => {
    return filteredSidebarMenu
      .filter(item => !item.hideOnMobile)
      .filter(({ id }) => (isLiveChatAgent ? id === 'INBOX' : true));
  }, [filteredSidebarMenu, isLiveChatAgent]);

  const closeMenu = () => setDropdownExpanded(false);

  const onSidebarMenuActionClick = () => {
    dispatch(chatbotSidebarActions.resetMenu());
    closeMenu();
  };

  let logoHref = '/';

  if (activeChatbotId && !isLiveChatAgent) {
    logoHref = BotBuilderRoutes.dashboard.getLink({
      chatbotId: activeChatbotId,
    });
  }

  const onUserDropdownChange = (isOpen: boolean) => {
    if (isOpen) {
      toggleMenuGroupExpanded(expandedMenuGroups[0]);
    }
  };

  return (
    <div className={classList}>
      {slideMenuExpanded && (
        <ActivePageSlideMenu
          onPageSelect={() => {
            setSlideMenuExpanded(false);
            setDropdownExpanded(false);
          }}
        />
      )}

      <div
        onClick={() => {
          setDropdownExpanded(!dropdownExpanded);
          setSlideMenuExpanded(false);
        }}
        className="mobile-header__hamburger"
      >
        <span className="mobile-header__line mobile-header__line-1" />
        <span className="mobile-header__line mobile-header__line-2" />
        <span className="mobile-header__line mobile-header__line-3" />
      </div>

      {!dropdownExpanded && (
        <Logo href={logoHref} className="mobile-header__logo" />
      )}

      {isAutomationsEnabled && !dropdownExpanded && (
        <AutomationTemplateContainer.Provider>
          <TemplateGoButton
            label="Automation"
            leftIcon={
              <PlusOutlined className="mobile-header__automation-button-icon" />
            }
            css={{
              container: 'mobile-header__automation-button-container',
              button: 'mobile-header__automation-button',
            }}
          />
        </AutomationTemplateContainer.Provider>
      )}

      {dropdownExpanded && (
        <>
          <div
            className={classnames('mobile-header__header-item-wrapper', {
              'mobile-header__header-item-wrapper--active':
                dropdownExpanded && !slideMenuExpanded,
            })}
            onClick={() => {
              setDropdownExpanded(true);
              setSlideMenuExpanded(false);
            }}
          >
            <HomeOutlined className="mobile-header__home-icon" />
          </div>
          <PageSwitcher
            onMenuClick={() => {
              setSlideMenuExpanded(!slideMenuExpanded);
            }}
            facebookPage={activeFacebookPage}
            isMobile={slideMenuExpanded}
          />
          <UserDropdown onChange={onUserDropdownChange} isMobile />
        </>
      )}

      <div className="mobile-header__dropdown">
        {company && showUpgradeRibbon && activeFacebookPage && (
          <UpgradeRibbon facebookPage={activeFacebookPage} company={company} />
        )}

        {mobileSidebarMenu.map(menuGroup => (
          <SidebarMenuGroup
            key={menuGroup.id}
            menuGroup={menuGroup}
            sidebarMinimized={false}
            toggleMenuGroupExpanded={toggleMenuGroupExpanded}
            expandedMenuGroups={expandedMenuGroups}
            activeChatbotId={activeChatbotId}
            guardedProLinkClick={() => ''}
            onMobileClick={closeMenu}
            disableMenuItems={menuGroup.id === 'SMS' && !twilioNumber}
            isMobile
          />
        ))}

        {isAutomationsEnabled && (
          <>
            <div className="sidebar-menu-group mobile-header__create-automation">
              <AutomationTemplateContainer.Provider>
                <TemplateGoButton
                  label="Automation"
                  leftIcon={
                    <PlusOutlined className="mobile-header__automation-button-icon" />
                  }
                  css={{
                    button: 'mobile-header__create-automation-button',
                  }}
                  onClick={closeMenu}
                />
              </AutomationTemplateContainer.Provider>
            </div>
            <div className="mobile-header__all-features-container">
              <AllFeaturesDropdown
                onItemClick={closeMenu}
                className="mobile-header__all-features"
                isMobile
              />
            </div>
          </>
        )}

        <div className="mobile-header__bottom-bar">
          {!isLiveChatAgent && !isAutomationsEnabled && (
            <SidebarMenuIconActions onClick={onSidebarMenuActionClick} />
          )}
        </div>
        <div
          className="mobile-header__logout"
          onClick={() => history.push(SIGNOUT)}
        >
          {' '}
          <LogoutOutlined className="mobile-header__logout-icon" />
          Sign out
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  toggleMenuGroupExpanded: (menuGroupId: string) =>
    dispatch(chatbotSidebarActions.toggleMenuGroupExpanded(menuGroupId)),
  fetchPage: (facebookPageId: number) =>
    dispatch(connectedPageCRUDActions.retrievePage(facebookPageId)),
  fetchBasicPageList: (companyId: number) =>
    dispatch(
      connectedPageCRUDActions.retrieveBasicListByCompany(companyId, {
        withFacebookErrors: true,
        withSubscription: true,
      }),
    ),
  retrieveChatbotsByPage: (facebookPageId: number) =>
    dispatch(chatbotCRUDActions.retrieveAll(facebookPageId)),
});

const mapStateToProps = (state: StoreState) => ({
  activeFacebookPage: activeFacebookPageSelector(state),
  filteredSidebarMenu: filteredSidebarMenuSelector(state),
  activeChatbotId: activeChatbotIdSelector(state),
  expandedMenuGroups: state.chatbotSidebarState.expandedMenuGroups,
  profileImage: state.session.session.image,
  session: state.session.session,
  company: activeCompanySelector(state),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(MobileHeader);
